import { useContext, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { MessageContext } from '@/helpers/MessageProvider';

const useFlightControlCommand = () => {
  const { publishCommand } = useContext(MessageContext);
  const stickControlSeq = useRef({});

  const brake = () => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/brake', [tid, bid, Date.now()]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/fly_to_point_stop`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const drcModeEnter = () => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();
    const expireTime = Math.floor(Date.now() / 1000) + 3600; // 1 hour

    const execute = () => {
      return publishCommand(robot, 'flight_control/drc_mode_enter', [
        tid,
        bid,
        Date.now(),
        `${process.env.REACT_APP_MQTT_DJI_HOST}:${process.env.REACT_APP_MQTT_DJI_PORT}`,
        robot.id,
        process.env.REACT_APP_MQTT_DJI_USERNAME,
        process.env.REACT_APP_MQTT_DJI_PASSWORD,
        expireTime,
      ]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/drc_mode_enter`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const drcModeExit = () => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/drc_mode_exit', [tid, bid, Date.now()]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/drc_mode_exit`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const goto = (height, latitude, longitude) => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();
    const flightId = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/goto', [
        tid,
        bid,
        Date.now(),
        flightId,
        [{ height, latitude, longitude }],
      ]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/fly_to_point`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const gotoUpdate = (height, latitude, longitude) => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/goto/update', [
        tid,
        bid,
        Date.now(),
        [{ height, latitude, longitude }],
      ]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/fly_to_point_update`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const heartbeat = (robot) => {
    publishCommand(robot, 'flight_control/heartbeat', [Date.now()]);
  };

  const returnHome = () => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/return_home', [tid, bid, Date.now()]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/return_home`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const stickControl = (robot, roll, pitch, throttle, yaw) => {
    let seq = stickControlSeq.current[robot.id];
    seq = seq === undefined ? 0 : (seq + 1) % 65536;
    stickControlSeq.current[robot.id] = seq;

    return publishCommand(robot, 'flight_control/stick_control', [seq, roll, pitch, throttle, yaw]);
  };

  const takeoff = (height, dockHeight, latitude, longitude) => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();
    const flightId = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/takeoff', [
        tid,
        bid,
        Date.now(),
        flightId,
        height, // commander_flight_height
        height, // security_takeoff_height
        height + dockHeight, // target_height
        latitude,
        longitude,
      ]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/takeoff_to_point`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  const takeoffSimulation = (height, dockHeight, latitude, longitude) => (robot) => {
    const tid = uuidv4();
    const bid = uuidv4();
    const flightId = uuidv4();

    const execute = () => {
      return publishCommand(robot, 'flight_control/takeoff/simulation', [
        tid,
        bid,
        Date.now(),
        flightId,
        height, // commander_flight_height
        height, // security_takeoff_height
        height + dockHeight, // target_height
        latitude, // target_latitude
        longitude, // target_longitude
        latitude, // simulate_mission.latitude
        longitude, // simulate_mission.longitude
      ]);
    };

    return {
      execute,
      topic: `${robot.id}/telemetry/takeoff_to_point`,
      isMatch: (data) => data.tid === tid && data.bid === bid,
      isSuccess: ({ data }) => data.result === 0,
    };
  };

  return {
    brake,
    drcModeEnter,
    drcModeExit,
    goto,
    gotoUpdate,
    heartbeat,
    returnHome,
    stickControl,
    takeoff,
    takeoffSimulation,
  };
};

export default useFlightControlCommand;
